import React, { useState, useContext, useMemo, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { PredContext } from './sportsc/predContext';

// --- STYLED COMPONENTS ---

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #0e141b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
`;

const StatWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 15px;
`;

const BackBtn = styled.button`
  background: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 10px;
`;

const GemToggleBtn = styled.button`
  background: #0e141b;
  color: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 700;
  text-transform: capitalize;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const FeatureContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin: 12px 0;
  padding: 12px;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding: 6px 8px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const FeatureLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
`;

const FeatureValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
`;

const DisplayDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 12px 0;
`;

const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 50vh;
  box-sizing: border-box;
  overflow: hidden; /* Prevents child canvas elements from forcing page overflow */
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button.attrs({ type: 'button' })`
  padding: 8px 14px;
  font-size: 0.825rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(ActionButton)`
  background-color: #ef4444;
  color: white;
`;

const SendButton = styled(ActionButton)`
  background-color: #16a34a;
  color: white;
`;

const SecondaryButton = styled(ActionButton)`
  background-color: #64748b;
  color: white;
`;

const LinesContainer = styled.div`
  background: white;
  margin: 0 1rem 1rem;
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 450px;
  overflow-y: auto;
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;

  th {
    padding: 8px;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    border-bottom: 2px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: white;
    user-select: none;

    &.filterable {
      cursor: pointer;

      &:hover {
        color: #0f172a;
        background-color: #f8fafc;
      }
    }
  }

  td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    font-weight: 500;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const ClickableCell = styled.td`
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #1d4ed8;
  }
`;

const ActiveFilterTag = styled.span`
  margin-left: 4px;
  padding: 2px 5px;
  background-color: #2563eb;
  color: white;
  border-radius: 4px;
  font-size: 0.65rem;
  text-transform: none;
`;

const Badge = styled.span`
  background: #e2e8f0;
  color: #0e141b;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const PickBadge = styled(Badge)`
  background: ${(props) => (props.$pick === 'OVER' ? '#dcfce7' : '#fee2e2')};
  color: ${(props) => (props.$pick === 'OVER' ? '#15803d' : '#b91c1c')};
`;

const EmptyText = styled.p`
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  margin: 1rem 0;
`;

// --- HELPER FUNCTIONS ---

function shortenValue(val) {
  if (typeof val === 'string' && isNaN(val)) return val;
  return Number(val).toFixed(2);
}

// --- PLAYER PAGE COMPONENT ---

const PlayerPage = ({ player, onBack, view = 'points', team = '' }) => {
  const GEMS = ['amethyst3', 'emerald3', 'sapphire3', 'amethyst1', 'emerald1', 'sapphire1'];

  const [gem, setGem] = useState('amethyst3');
  const [windowSize, setWindowSize] = useState(10);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  const [allPredData, setAllPredData] = useState({});
  const [allTodayPreds, setAllTodayPreds] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [allFeatures, setAllFeatures] = useState({});

  const GEM_COLORS = {
    rock: { border: 'rgb(107, 114, 128)', bg: 'rgba(107, 114, 128, 0.5)' },
    ruby: { border: 'rgb(225, 29, 72)', bg: 'rgba(225, 29, 72, 0.5)' },
    diamond: { border: 'rgb(56, 189, 248)', bg: 'rgba(56, 189, 248, 0.5)' },
    quartz: { border: 'rgb(236, 72, 153)', bg: 'rgba(236, 72, 153, 0.5)' },
    beryl: { border: 'rgb(16, 185, 129)', bg: 'rgba(16, 185, 129, 0.5)' },
    mica: { border: 'rgb(245, 158, 11)', bg: 'rgba(245, 158, 11, 0.5)' },
  };

  const fetchGemTodayPred = async (selectedPlayer, targetView, activeGem) => {
    const res = await axios({
      method: 'post',
      url: `https://82jsvqje43.execute-api.us-east-2.amazonaws.com/wnba_dev/nba`,
      withCredentials: false,
      data: {
        player: selectedPlayer,
        path: targetView,
        gem: activeGem,
      },
    });
    const numbers_res = res.data.match(/[\d.]+/g);
    return numbers_res ? Number(numbers_res[0]) : null;
  };

  const fetchGemFeatures = async (targetView, activeGem) => {
    const res = await axios({
      method: 'post',
      url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
      withCredentials: false,
      data: {
        option: 'features',
        target: targetView,
        gem: activeGem,
      },
    });
    return res.data;
  };

  const fetchGemChartData = async (selectedPlayer, activeGem, teamStr) => {
    try {
      if (!teamStr || !teamStr.includes('_')) return null;
      const parts = teamStr.split('_');
      if (parts.length < 3) return null;

      const month = parts[1];
      const day = parts[2].slice(0, 2);

      const res = await axios({
        method: 'post',
        url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
        withCredentials: false,
        data: {
          option: 'player-pred',
          player: selectedPlayer,
          day,
          month,
          gem: activeGem,
        },
      });

      return res.data?.data || null;
    } catch (err) {
      console.error(`Chart data error [${activeGem}]:`, err);
      return null;
    }
  };

  useEffect(() => {
    let isSubscribed = true;

    const fetchAllGems = async () => {
      setIsLoading(true);

      const gemPromises = GEMS.map(async (g) => {
        const [todayRes, featRes, chartRes] = await Promise.allSettled([
          fetchGemTodayPred(player, view, g),
          fetchGemFeatures(view, g),
          fetchGemChartData(player, g, team),
        ]);

        if (!isSubscribed) return;

        if (todayRes.status === 'fulfilled') {
          setAllTodayPreds((prev) => ({ ...prev, [g]: todayRes.value }));
        }
        if (featRes.status === 'fulfilled') {
          const data = featRes.value?.data || featRes.value || [];
          const formatted = Array.isArray(data) ? data : Object.entries(data);
          setAllFeatures((prev) => ({ ...prev, [g]: formatted }));
        }
        if (chartRes.status === 'fulfilled') {
          setAllPredData((prev) => ({ ...prev, [g]: chartRes.value }));
        }
      });

      await Promise.allSettled(gemPromises);

      if (isSubscribed) {
        setIsLoading(false);
      }
    };

    fetchAllGems();

    return () => {
      isSubscribed = false;
    };
  }, [player, view, team]);

  const cycleGem = () => {
    const currentIndex = GEMS.indexOf(gem);
    const nextGem = GEMS[(currentIndex + 1) % GEMS.length];
    setGem(nextGem);
  };

  const playerChartData = useMemo(() => {
    const activeData = allPredData[gem];
    const rawList = activeData && activeData[view] ? activeData[view] : [];

    if (!Array.isArray(rawList) || rawList.length === 0) return [];

    const parseValue = (val) => {
      if (val === null || val === undefined) return null;
      if (Array.isArray(val)) return Number(val[0]);
      if (typeof val === 'string') {
        const cleaned = val.replace(/[\[\]\s]/g, '');
        return cleaned === '' ? null : Number(cleaned);
      }
      return Number(val);
    };

    return rawList
      .filter((row) => row[0] !== '0')
      .map((row, i) => ({
        date: row[0],
        pred: parseValue(row[1]),
        actual: parseValue(row[2]),
        label: row[0],
        _sortKey: i,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date) || a._sortKey - b._sortKey);
  }, [allPredData, gem, view]);

  useEffect(() => {
    if (playerChartData.length === 0) return;
    const end = playerChartData.length - 1;
    const start = Math.max(0, end - windowSize + 1);
    setStartIndex(start);
    setEndIndex(end);
  }, [playerChartData.length, windowSize]);

  const handleBack = () => {
    setStartIndex((prev) => Math.max(0, prev - 1));
    setEndIndex((prev) => Math.max(windowSize - 1, prev - 1));
  };

  const handleForward = () => {
    setStartIndex((prev) => Math.min(playerChartData.length - windowSize, prev + 1));
    setEndIndex((prev) => Math.min(playerChartData.length - 1, prev + 1));
  };

  const isBackDisabled = startIndex === 0;
  const isForwardDisabled = endIndex >= playerChartData.length - 1;

  const cycleWindowSize = () => {
    setWindowSize((prev) => (prev >= 20 ? 5 : prev + 5));
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    scales: { x: { ticks: { autoSkip: false } } },
  };

  const chartDataForPlayer = useMemo(() => {
    if (playerChartData.length === 0) return { labels: [], datasets: [] };

    const visible = playerChartData.slice(startIndex, endIndex + 1);
    const currentColor = GEM_COLORS[gem] || GEM_COLORS.rock;

    return {
      labels: visible.map((d) => d.date),
      datasets: [
        {
          label: `Predicted (${gem.toUpperCase()})`,
          data: visible.map((d) => d.pred),
          borderColor: currentColor.border,
          backgroundColor: currentColor.bg,
          fill: false,
          tension: 0.2,
        },
        {
          label: 'Actual',
          data: visible.map((d) => d.actual),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          fill: false,
          tension: 0.2,
        },
      ],
    };
  }, [playerChartData, startIndex, endIndex, gem]);

  const currentTodayPred = allTodayPreds[gem];
  const currentFeatures = allFeatures[gem] || [];
  const EXCLUDED_METRICS = ['y_train_range', 'y_train_std', 'y_test_range', 'y_true'];

  return (
    <div style={{ padding: '1rem' }}>
      <DisplayDiv>
        <h2 style={{ marginTop: '10px' }}>{player}</h2>
        <BackBtn onClick={onBack}>← Back</BackBtn>
        <StatWrapper>
          <h3 style={{ margin: 0 }}>Today's Pred:</h3>
          {isLoading ? <Spinner /> : <h4 style={{ margin: 0 }}>{currentTodayPred}</h4>}
        </StatWrapper>

        <GemToggleBtn onClick={cycleGem} disabled={isLoading}>
          {isLoading ? 'Loading all gems...' : gem}
        </GemToggleBtn>

        <button onClick={handleBack} disabled={isBackDisabled || isLoading}>
          ← Back
        </button>
        <button onClick={handleForward} disabled={isForwardDisabled || isLoading}>
          Forward →
        </button>
        <button onClick={cycleWindowSize} disabled={isLoading}>
          Show last {windowSize}
        </button>
      </DisplayDiv>

      {!isLoading && currentFeatures.length > 0 && (
        <FeatureContainer>
          {currentFeatures
            .filter(([metricKey]) => !EXCLUDED_METRICS.includes(metricKey))
            .map(([metricKey, val]) => (
              <FeatureItem key={metricKey}>
                <FeatureLabel>{metricKey}</FeatureLabel>
                <FeatureValue>{shortenValue(val)}</FeatureValue>
              </FeatureItem>
            ))}
        </FeatureContainer>
      )}

      <ChartWrapper style={{ opacity: isLoading ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
        <Line data={chartDataForPlayer} options={options} width="80vw" height="50vh" />
      </ChartWrapper>
    </div>
  );
};

// --- LINES VIEW COMPONENT ---

function LinesView({ onSendLines }) {
  const { lines, clearLines } = useContext(PredContext);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selected player for PlayerPage view
  const [selectedPlayerObj, setSelectedPlayerObj] = useState(null);

  // Multi-column filter state
  const [filters, setFilters] = useState({});

  // Cycle through unique values for a column key safely
  const handleCycleFilter = (key, dataset, getValueFn) => {
    if (!Array.isArray(dataset) || dataset.length === 0) return;

    const uniqueValues = Array.from(
      new Set(dataset.map(getValueFn).filter((v) => v !== undefined && v !== null && v !== ''))
    );

    if (uniqueValues.length === 0) return;

    setFilters((prev) => {
      const currentValue = prev[key];

      if (currentValue === undefined) {
        return { ...prev, [key]: uniqueValues[0] };
      }

      const currentIndex = uniqueValues.indexOf(currentValue);
      if (currentIndex !== -1 && currentIndex < uniqueValues.length - 1) {
        return { ...prev, [key]: uniqueValues[currentIndex + 1] };
      }

      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  // Flatten input lines safely
  const formattedLines = useMemo(() => {
    if (!lines || typeof lines !== 'object') return [];

    const result = [];
    Object.entries(lines).forEach(([statType, playersMap]) => {
      if (!playersMap || typeof playersMap !== 'object') return;
      Object.entries(playersMap).forEach(([player, data]) => {
        if (!data) return;
        result.push({
          player,
          type: statType,
          line: data.line ?? 'N/A',
          team: data.team ? String(data.team).replace(/\.csv$/, '') : 'N/A',
        });
      });
    });

    return result;
  }, [lines]);

  // Filtered Input Lines
  const filteredInputLines = useMemo(() => {
    if (!Array.isArray(formattedLines)) return [];
    const activeKeys = Object.keys(filters);
    if (activeKeys.length === 0) return formattedLines;

    return formattedLines.filter((item) =>
      activeKeys.every((key) => String(item[key]) === String(filters[key]))
    );
  }, [formattedLines, filters]);

  // Column index mapping for API results
  const resultColumnMap = {
    player: 0,
    metric: 2,
    model: 1,
    pred: 3,
    line: 4,
    edge: 5,
    pick: 7,
    winProb: 8,
  };

  // Filtered Results safely guarded against non-array structures
  const filteredResults = useMemo(() => {
    if (!Array.isArray(results)) return [];
    const activeKeys = Object.keys(filters);
    if (activeKeys.length === 0) return results;

    return results.filter((row) => {
      if (!Array.isArray(row)) return false;
      return activeKeys.every((key) => {
        const targetIdx = resultColumnMap[key];
        if (targetIdx === undefined) return true;
        return String(row[targetIdx] ?? '') === String(filters[key] ?? '');
      });
    });
  }, [results, filters]);

  // Direct API fetch helper
  const fetchLinesApi = async (payload) => {
    const res = await axios({
      method: 'post',
      url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
      withCredentials: false,
      data: {
        option: 'lines',
        players: payload,
      },
    });

    return res.data?.data;
  };

  // Unified Async Send Handler
  const handleSend = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      let data = null;

      if (typeof onSendLines === 'function') {
        const customResult = await onSendLines(formattedLines);
        if (Array.isArray(customResult)) {
          data = customResult;
        }
      }

      if (!data) {
        data = await fetchLinesApi(formattedLines);
      }

      if (Array.isArray(data)) {
        setResults(data);
        setFilters({});
      } else {
        setError('Invalid response payload format from server.');
      }
    } catch (err) {
      console.error('Error fetching lines:', err);
      setError('Failed to fetch predictions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = (e) => {
    if (e) e.preventDefault();
    setResults(null);
    setFilters({});
    clearLines();
  };

  const renderFilterIndicator = (key) => {
    if (filters[key]) {
      return <ActiveFilterTag>{filters[key]}</ActiveFilterTag>;
    }
    return null;
  };

  // 0. Render Player Detail View if Selected
  if (selectedPlayerObj) {
    return (
      <div>
      <PlayerPage
        player={selectedPlayerObj.player}
        view={selectedPlayerObj.view}
        team={selectedPlayerObj.team}
        onBack={() => setSelectedPlayerObj(null)}
      />
      </div>
    );
  }

  // 1. Loading State
  if (loading) {
    return (
      <LinesContainer>
        <EmptyText>Fetching predictions...</EmptyText>
      </LinesContainer>
    );
  }

  // 2. Render Results View
  if (Array.isArray(results)) {
    return (
      <LinesContainer>
        {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
        <LinesTable>
          <thead>
            <tr>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('player', results, (r) => r[resultColumnMap.player])
                }
              >
                Player{renderFilterIndicator('player')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('metric', results, (r) => r[resultColumnMap.metric])
                }
              >
                Metric{renderFilterIndicator('metric')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('model', results, (r) => r[resultColumnMap.model])
                }
              >
                Model{renderFilterIndicator('model')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('pred', results, (r) => r[resultColumnMap.pred])
                }
              >
                Pred{renderFilterIndicator('pred')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('line', results, (r) => r[resultColumnMap.line])
                }
              >
                Line{renderFilterIndicator('line')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('edge', results, (r) => r[resultColumnMap.edge])
                }
              >
                Edge{renderFilterIndicator('edge')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('pick', results, (r) => r[resultColumnMap.pick])
                }
              >
                Pick{renderFilterIndicator('pick')}
              </th>
              <th
                className="filterable"
                onClick={() =>
                  handleCycleFilter('winProb', results, (r) => r[resultColumnMap.winProb])
                }
              >
                Win %{renderFilterIndicator('winProb')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((row, idx) => {
              if (!Array.isArray(row)) return null;

              const [
                player = 'N/A',
                model = 'N/A',
                metric = 'N/A',
                prediction = 0,
                line = 0,
                edge = 0,
                rmse = 0,
                pick = 'N/A',
                winProb = 0,
              ] = row;

              const numPred = Number(prediction);
              const numEdge = Number(edge);
              const numWinProb = Number(winProb);

              return (
                <tr key={`${player}-${model}-${idx}`}>
                  <ClickableCell
                    onClick={() =>
                      setSelectedPlayerObj({
                        player,
                        view: metric.toLowerCase(),
                        team: '',
                      })
                    }
                  >
                    {player}
                  </ClickableCell>
                  <td>
                    <Badge>{metric}</Badge>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{model}</td>
                  <td>{!isNaN(numPred) ? numPred.toFixed(2) : 'N/A'}</td>
                  <td style={{ fontWeight: 700 }}>{line}</td>
                  <td
                    style={{
                      color: !isNaN(numEdge) && numEdge > 0 ? '#16a34a' : '#dc2626',
                      fontWeight: 600,
                    }}
                  >
                    {!isNaN(numEdge) ? (numEdge > 0 ? `+${numEdge}` : numEdge) : 'N/A'}
                  </td>
                  <td>
                    <PickBadge $pick={pick}>{pick}</PickBadge>
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    {!isNaN(numWinProb) ? `${(numWinProb * 100).toFixed(1)}%` : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </LinesTable>

        <ActionRow>
          <SecondaryButton
            onClick={(e) => {
              if (e) e.preventDefault();
              setResults(null);
              setFilters({});
            }}
          >
            Back to Input
          </SecondaryButton>
          <ClearButton onClick={handleClearAll}>Clear All</ClearButton>
        </ActionRow>
      </LinesContainer>
    );
  }

  // 3. Render Empty State
  if (formattedLines.length === 0) {
    return (
      <LinesContainer>
        <EmptyText>No active lines submitted yet.</EmptyText>
      </LinesContainer>
    );
  }

  // 4. Default Input Lines View
  return (
    <LinesContainer>
      {error && <EmptyText style={{ color: '#ef4444' }}>{error}</EmptyText>}
      <LinesTable>
        <thead>
          <tr>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('player', formattedLines, (i) => i.player)}
            >
              Player{renderFilterIndicator('player')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('type', formattedLines, (i) => i.type)}
            >
              Type{renderFilterIndicator('type')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('line', formattedLines, (i) => i.line)}
            >
              Line{renderFilterIndicator('line')}
            </th>
            <th
              className="filterable"
              onClick={() => handleCycleFilter('team', formattedLines, (i) => i.team)}
            >
              Team{renderFilterIndicator('team')}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredInputLines.map((item, idx) => (
            <tr key={`${item.player}-${item.type}-${idx}`}>
              <ClickableCell
                onClick={() =>
                  setSelectedPlayerObj({
                    player: item.player,
                    view: item.type.toLowerCase(),
                    team: item.team,
                  })
                }
              >
                {item.player}
              </ClickableCell>
              <td>
                <Badge>{item.type}</Badge>
              </td>
              <td style={{ fontWeight: 700, color: '#16a34a' }}>{item.line}</td>
              <td>{item.team}</td>
            </tr>
          ))}
        </tbody>
      </LinesTable>

      <ActionRow>
        <ClearButton
          onClick={(e) => {
            if (e) e.preventDefault();
            clearLines();
          }}
        >
          Clear Lines
        </ClearButton>
        <SendButton onClick={handleSend} disabled={loading}>
          Send Lines
        </SendButton>
      </ActionRow>
    </LinesContainer>
  );
}

export default LinesView;